import React, { useState, useEffect, useCallback } from 'react';
import '../components/booth/tokens.css';
import '../styles/adminDashboard.css';
import MainBar from '../components/mainBar';
import cronstrue from 'cronstrue';
import { formatRelative, format } from 'date-fns';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://thebooth.gg'
    : 'http://localhost:8000';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Los_Angeles',
    timeZoneName: 'short',
  });
}

function avgDuration(runs) {
  const completed = runs.filter((r) => r.duration_ms > 0);
  if (!completed.length) return '—';
  const avg = completed.reduce((s, r) => s + r.duration_ms, 0) / completed.length;
  return (avg / 1000).toFixed(1) + 's';
}

function totalEmails(runs) {
  return runs.reduce((s, r) => s + (r.emails_sent || 0), 0);
}

function totalLeagues(runs) {
  return runs.reduce((s, r) => s + (r.leagues_processed || 0), 0);
}

function badgeClass(status) {
  if (status === 'success') return 'booth-admin-badge booth-admin-badge--success';
  if (status === 'partial') return 'booth-admin-badge booth-admin-badge--partial';
  if (status === 'failed') return 'booth-admin-badge booth-admin-badge--failed';
  return 'booth-admin-badge booth-admin-badge--running';
}

function badgeLabel(status) {
  if (status === 'success') return 'Success';
  if (status === 'partial') return 'Partial fail';
  if (status === 'failed') return 'Failed';
  return 'Running';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function EmptyState({ message }) {
  return (
    <div className="booth-admin-empty">
      <span className="booth-admin-empty-icon">—</span>
      <span>{message}</span>
    </div>
  );
}

function CronRunLog({ runs, loading }) {
  if (loading) return <EmptyState message="Loading..." />;
  if (!runs.length) return <EmptyState message="No pipeline runs recorded yet. The first run will appear here after the Tuesday cron fires." />;

  return (
    <div>
      {runs.map((run) => {
        const durationSec = run.duration_ms ? (run.duration_ms / 1000).toFixed(1) + 's' : '—';
        const emailsFailed = run.emails_failed || 0;
        return (
          <div className="booth-admin-row" key={run.id}>
            <div className="booth-admin-row-info">
              <div className="booth-admin-row-label">Recap pipeline</div>
              <div className="booth-admin-row-meta">
                <span className="booth-admin-row-meta-item">
                  {run.leagues_processed ?? 0} {run.leagues_processed === 1 ? 'league' : 'leagues'}
                </span>
                <span className="booth-admin-row-meta-dot" />
                <span className="booth-admin-row-meta-item">
                  {run.emails_sent ?? 0} sent{emailsFailed > 0 ? `, ${emailsFailed} failed` : ''}
                </span>
                <span className="booth-admin-row-meta-dot" />
                <span className="booth-admin-row-meta-item">{durationSec}</span>
              </div>
            </div>
            <div className="booth-admin-row-right">
              <span className="booth-admin-row-date">{formatDate(run.started_at)}</span>
              <span className={badgeClass(run.status)}>
                <span className="booth-admin-badge-dot" />
                {badgeLabel(run.status)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Schedule helpers ─────────────────────────────────────────────────────────

const JOB_LABELS = {
  daily_intelligence: 'Daily Intelligence',
  tuesday_recap: 'Tuesday Recap',
};

function formatLastRun(isoString) {
  if (!isoString) return '—';
  try {
    return formatRelative(new Date(isoString), new Date());
  } catch {
    return '—';
  }
}

function formatNextRun(isoString) {
  if (!isoString) return '—';
  try {
    return format(new Date(isoString), "EEE MMM d h:mmaaa");
  } catch {
    return '—';
  }
}

function humanCron(expression) {
  try {
    return cronstrue.toString(expression);
  } catch {
    return expression;
  }
}

function scheduleStatusBadge(status) {
  if (!status) return <span className="booth-admin-badge booth-admin-badge--neutral"><span className="booth-admin-badge-dot" />—</span>;
  return (
    <span className={badgeClass(status)}>
      <span className="booth-admin-badge-dot" />
      {badgeLabel(status)}
    </span>
  );
}

function CronScheduleRow({ schedule }) {
  const label = JOB_LABELS[schedule.job_name] || schedule.job_name;
  const durationSec = schedule.last_run_duration_ms
    ? (schedule.last_run_duration_ms / 1000).toFixed(1) + 's'
    : '—';

  return (
    <div className="booth-admin-row booth-admin-row--align-start">
      <div className="booth-admin-row-info">
        <div className="booth-admin-row-label">
          {label}
          {schedule.is_active ? (
            <span className="booth-admin-badge booth-admin-badge--success booth-admin-badge--sm">
              <span className="booth-admin-badge-dot" />
              Active
            </span>
          ) : (
            <span className="booth-admin-badge booth-admin-badge--neutral booth-admin-badge--sm">
              <span className="booth-admin-badge-dot" />
              Paused
            </span>
          )}
        </div>
        <div className="booth-admin-row-meta">
          <span className="booth-admin-row-meta-item">{schedule.description}</span>
        </div>
        <div className="booth-admin-row-meta">
          <span className="booth-admin-row-meta-item">{humanCron(schedule.cron_expression)}</span>
          <span className="booth-admin-row-meta-dot" />
          <span className="booth-admin-row-meta-item">Last: {formatLastRun(schedule.last_run_at)}</span>
          <span className="booth-admin-row-meta-dot" />
          <span className="booth-admin-row-meta-item">
            Next:{' '}
            {schedule.overdue ? (
              <>
                {formatNextRun(schedule.next_run_at)}{' '}
                <span className="booth-admin-badge booth-admin-badge--partial booth-admin-badge--sm">
                  <span className="booth-admin-badge-dot" />
                  Overdue
                </span>
              </>
            ) : (
              formatNextRun(schedule.next_run_at)
            )}
          </span>
        </div>
        <div className="booth-admin-row-meta">
          {schedule.last_triggered_by && (
            <span className="booth-admin-row-meta-item booth-admin-row-meta-trigger">
              via {schedule.last_triggered_by}
            </span>
          )}
          {schedule.last_run_duration_ms !== null && (
            <>
              <span className="booth-admin-row-meta-dot" />
              <span className="booth-admin-row-meta-item">{durationSec}</span>
            </>
          )}
        </div>
      </div>
      <div className="booth-admin-row-right">
        {scheduleStatusBadge(schedule.last_run_status)}
      </div>
    </div>
  );
}

function CronSchedulePanel({ API_BASE }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSchedules = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/admin/cron-schedules`)
      .then((r) => r.json())
      .then((data) => {
        setSchedules(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setSchedules([]);
        setLoading(false);
      });
  }, [API_BASE]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return (
    <section className="booth-admin-section">
      <div className="booth-admin-section-header">
        <span className="booth-admin-section-title">Cron schedules</span>
        <button className="booth-btn booth-btn--secondary booth-btn--sm" onClick={fetchSchedules}>
          Refresh
        </button>
      </div>
      {loading ? (
        <EmptyState message="Loading..." />
      ) : schedules.length === 0 ? (
        <EmptyState message="No schedule records found." />
      ) : (
        schedules.map((s) => <CronScheduleRow key={s.job_name} schedule={s} />)
      )}
    </section>
  );
}

function WritingSection({ selectedWeek, onWeekChange, writings, loading, availableWeeks, weeksLoading }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const activeRecap = writings[activeIdx];

  useEffect(() => {
    setActiveIdx(0);
  }, [selectedWeek]);

  const showWeekSelector = !weeksLoading && availableWeeks.length > 0;

  return (
    <section className="booth-admin-section">
      <div className="booth-admin-section-header">
        <span className="booth-admin-section-title">Writing output</span>
        {showWeekSelector && (
          <div className="booth-admin-tab-row" style={{ marginBottom: 0 }}>
            {availableWeeks.map((w) => (
              <button
                key={w}
                className={`booth-admin-tab${selectedWeek === w ? ' booth-admin-tab--active' : ''}`}
                onClick={() => onWeekChange(w)}
              >
                Wk {w}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <EmptyState message="Loading..." />
      ) : writings.length === 0 ? (
        <EmptyState message={availableWeeks.length === 0 ? "No writing output yet. Recaps will appear here after the first pipeline run." : `No writing output for week ${selectedWeek}.`} />
      ) : (
        <>
          <div className="booth-admin-tab-row">
            {writings.map((recap, i) => (
              <button
                key={recap.id}
                className={`booth-admin-tab${activeIdx === i ? ' booth-admin-tab--active' : ''}`}
                onClick={() => setActiveIdx(i)}
              >
                {recap.home_team_name} vs. {recap.away_team_name}
              </button>
            ))}
          </div>
          {activeRecap && (
            <>
              <div className="booth-admin-writing-meta">
                {activeRecap.leagues?.name} · Wk {activeRecap.week} · {activeRecap.home_team_name} {activeRecap.home_score?.toFixed(2)} – {activeRecap.away_team_name} {activeRecap.away_score?.toFixed(2)}
              </div>
              <div className="booth-admin-writing-body">{activeRecap.writing}</div>
            </>
          )}
        </>
      )}
    </section>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function AdminDashboard() {
  const [cronRuns, setCronRuns] = useState([]);
  const [cronRunsLoading, setCronRunsLoading] = useState(true);

  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [weeksLoading, setWeeksLoading] = useState(true);
  const [writingWeek, setWritingWeek] = useState(null);
  const [writingData, setWritingData] = useState([]);
  const [writingLoading, setWritingLoading] = useState(false);

  const fetchCronRuns = useCallback(() => {
    setCronRunsLoading(true);
    fetch(`${API_BASE_URL}/admin/cron-runs`)
      .then((r) => r.json())
      .then((data) => {
        setCronRuns(Array.isArray(data) ? data : []);
        setCronRunsLoading(false);
      })
      .catch(() => {
        setCronRuns([]);
        setCronRunsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchCronRuns();
  }, [fetchCronRuns]);

  useEffect(() => {
    setWeeksLoading(true);
    fetch(`${API_BASE_URL}/admin/recaps/weeks`)
      .then((r) => r.json())
      .then((data) => {
        const weeks = Array.isArray(data) ? data : [];
        setAvailableWeeks(weeks);
        if (weeks.length > 0) setWritingWeek(weeks[0]);
        setWeeksLoading(false);
      })
      .catch(() => {
        setAvailableWeeks([]);
        setWeeksLoading(false);
      });
  }, []);

  useEffect(() => {
    if (writingWeek === null) return;
    setWritingLoading(true);
    fetch(`${API_BASE_URL}/admin/recaps?week=${writingWeek}`)
      .then((r) => r.json())
      .then((data) => {
        setWritingData(Array.isArray(data) ? data : []);
        setWritingLoading(false);
      })
      .catch(() => {
        setWritingData([]);
        setWritingLoading(false);
      });
  }, [writingWeek]);

  const completedRuns = cronRuns.filter((r) => r.status !== 'running');
  const latestRun = cronRuns[0];
  const successCount = cronRuns.filter((r) => r.status === 'success').length;
  const partialCount = cronRuns.filter((r) => r.status === 'partial').length;

  return (
    <div className="booth booth-admin">
      <MainBar />
      <main className="booth-admin-main">

        {/* Header */}
        <div className="booth-admin-header">
          <h1 className="booth-admin-title">Admin</h1>
          <div className="booth-admin-dek">
            NFL 2025 Season · {completedRuns.length} pipeline runs · Last run: {latestRun ? formatDate(latestRun.started_at) : '—'}
          </div>
        </div>

        {/* Metrics */}
        <div className="booth-admin-metrics">
          <div className="booth-admin-metric">
            <div className="booth-admin-metric-label">Cron runs</div>
            <div className="booth-admin-metric-value">{completedRuns.length}</div>
          </div>
          <div className="booth-admin-metric">
            <div className="booth-admin-metric-label">Leagues processed</div>
            <div className="booth-admin-metric-value">{totalLeagues(completedRuns)}</div>
          </div>
          <div className="booth-admin-metric">
            <div className="booth-admin-metric-label">Emails sent</div>
            <div className="booth-admin-metric-value">{totalEmails(completedRuns)}</div>
          </div>
          <div className="booth-admin-metric">
            <div className="booth-admin-metric-label">Avg duration</div>
            <div className="booth-admin-metric-value">{avgDuration(completedRuns)}</div>
          </div>
        </div>

        {/* Summary pills */}
        {completedRuns.length > 0 && (
          <div className="booth-admin-summary">
            <span className="booth-admin-badge booth-admin-badge--success">
              <span className="booth-admin-badge-dot" />
              {successCount} successful
            </span>
            {partialCount > 0 && (
              <span className="booth-admin-badge booth-admin-badge--partial">
                <span className="booth-admin-badge-dot" />
                {partialCount} partial failures
              </span>
            )}
          </div>
        )}

        {/* Cron run log */}
        <section className="booth-admin-section">
          <div className="booth-admin-section-header">
            <span className="booth-admin-section-title">Pipeline run log</span>
          </div>
          <CronRunLog runs={cronRuns} loading={cronRunsLoading} />
        </section>

        {/* Cron schedules */}
        <CronSchedulePanel API_BASE={API_BASE_URL} />

        {/* Writing */}
        <WritingSection
          selectedWeek={writingWeek}
          onWeekChange={setWritingWeek}
          writings={writingData}
          loading={writingLoading}
          availableWeeks={availableWeeks}
          weeksLoading={weeksLoading}
        />

      </main>
    </div>
  );
}

export default AdminDashboard;
